import { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

interface ActionDropdownProps {
  onPreInspection: () => void;
  onIncident: () => void;
  studentName: string;
}

export default function ActionDropdown({ onPreInspection, onIncident, studentName }: ActionDropdownProps) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    if (open) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [open]);

  return (
    <div ref={ref} className="relative">
      <button
        onClick={(e) => {
          e.stopPropagation();
          setOpen(!open);
        }}
        className="w-8 h-8 flex items-center justify-center rounded-full bg-background-100 text-foreground-500 hover:bg-secondary-100 hover:text-secondary-700 transition-colors cursor-pointer whitespace-nowrap"
        title={t('attendance.actionsFor', { name: studentName })}
      >
        <i className="ri-more-2-fill text-sm" />
      </button>

      {open && (
        <div className="absolute right-0 top-full mt-2 w-64 bg-background-50 rounded-xl border border-background-200 shadow-floating z-30 py-2 animate-[modalEnter_180ms_cubic-bezier(0.4,0,0.2,1)]">
          <p className="px-3 py-1.5 text-xs font-label font-semibold text-foreground-500 uppercase tracking-wide">
            {studentName}
          </p>
          <div className="h-px bg-background-200 mx-2 my-1" />

          <button
            onClick={(e) => {
              e.stopPropagation();
              setOpen(false);
              onPreInspection();
            }}
            className="w-full flex items-center gap-3 px-3 py-2.5 hover:bg-background-100 transition-colors cursor-pointer group text-left"
          >
            <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-blue-50 group-hover:bg-blue-100 transition-colors">
              <i className="ri-clipboard-line text-blue-600 text-sm" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-xs font-label font-medium text-foreground-700">{t('attendance.preInspectionTransitLogging')}</p>
              <p className="text-[10px] text-foreground-400 font-label">{t('attendance.preInspectionDescription')}</p>
            </div>
            <i className="ri-arrow-right-s-line text-foreground-400 text-sm flex-shrink-0" />
          </button>

          <button
            onClick={(e) => {
              e.stopPropagation();
              setOpen(false);
              onIncident();
            }}
            className="w-full flex items-center gap-3 px-3 py-2.5 hover:bg-background-100 transition-colors cursor-pointer group text-left"
          >
            <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-red-50 group-hover:bg-red-100 transition-colors">
              <i className="ri-alert-line text-red-600 text-sm" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-xs font-label font-medium text-foreground-700">{t('attendance.classroomIncidentEmergency')}</p>
              <p className="text-[10px] text-foreground-400 font-label">{t('attendance.incidentDescription')}</p>
            </div>
            <i className="ri-arrow-right-s-line text-foreground-400 text-sm flex-shrink-0" />
          </button>
        </div>
      )}
    </div>
  );
}